<template>
  <div class="article">
    <slot>
      <ClientOnly v-if="editMode">
        <ArticleEditor v-model="content" />
      </ClientOnly>
      <div v-html="renderedHtml" v-else />
    </slot>
  </div>
</template>

<script setup lang="ts">
  import ArticleEditor from "./editor/ArticleEditor.vue";
  import * as cheerio from "cheerio";

  export interface ArticleProps {
    editMode?: boolean;
  }

  const props = withDefaults(defineProps<ArticleProps>(), {
    editMode: false,
  });

  const content = defineModel<string | null>("content", { required: false });

  const { getVariables } = useWebsiteContent();
  const { data: variables, suspense: variablesSuspense } = getVariables();
  await variablesSuspense();

  const renderedHtml = computed<string>(() => {
    if (!content.value || props.editMode) {
      return "";
    }

    const variablesValue = variables.value;

    const $ = cheerio.load(content.value);

    $('[data-type="variable"]').each((_, element) => {
      const id = $(element).attr("data-id");
      if (
        id &&
        variablesValue?.[id as keyof typeof variablesValue] !== undefined
      ) {
        $(element).text(variablesValue[id as keyof typeof variablesValue].toLocaleString("en-US"));
      }
    });

    const updatedHtml = $.html();

    return updatedHtml;
  });
</script>

<style lang="scss">
  .article {
    font-family: var(--font-family-main);
    color: #212529;
    font-size: 18px;
    line-height: 1.75;
    // max-width: 75ch;
    margin: 0 auto;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-weight: 700;
      line-height: 1.2;
      margin: 1.5em 0 0.5em;

      &:first-child {
        margin-top: 0;
      }
    }

    h1 {
      font-size: 2.5em;
    }

    h2 {
      font-size: 2em;
    }

    h3 {
      font-size: 1.75em;
    }

    h4 {
      font-size: 1.5em;
    }

    h5 {
      font-size: 1em;
    }

    h6 {
      font-size: 1em;
    }

    p {
      margin: 0.5em 0;

      &:first-child {
        margin-top: 0;
      }
    }

    ul,
    ol {
      margin: 0.5em 0;
      padding-left: 1.5em;

      p {
        margin: 0;
      }
    }

    li {
      margin: 0.125em 0;
    }

    ul {
      list-style-type: disc;

      ul {
        list-style-type: circle;
      }
    }

    ol {
      list-style-type: decimal;
    }

    a {
      color: #0066cc;
      text-decoration: underline;
      text-underline-offset: 0.2em;

      &:hover {
        color: #004c99;
      }
    }

    blockquote {
      margin: 1.5em 0;
      padding: 1em 1.5em;
      border-left: 4px solid #e9ecef;
      font-style: italic;
      color: #495057;
    }

    code {
      font-family: ui-monospace, monospace;
      font-size: 0.9em;
      background: #f8f9fa;
      padding: 0.2em 0.4em;
      border-radius: 3px;
    }

    pre {
      background: #f8f9fa;
      padding: 1em;
      border-radius: 4px;
      overflow-x: auto;

      code {
        background: none;
        padding: 0;
      }
    }

    img {
      width: 100%;
      height: auto;
      margin: 1.5em 0;
      border-radius: 16px;
    }

    hr {
      margin: 2em 0;
      border: 0;
      border-top: 1px solid #dee2e6;
    }
  }

  // .article {
  //   margin: 0 32px;

  //   overflow: hidden;

  //   font-family: var(--font-family-main);
  //   color: #212529;

  //   font-size: 24px;
  //   line-height: 1.6em;

  //   p,
  //   ol,
  //   h1,
  //   h2,
  //   h3,
  //   h4,
  //   h5,
  //   h6 {
  //     padding: 0.4em 0;
  //   }

  //   h1,
  //   h2,
  //   h3,
  //   h4,
  //   h5,
  //   h6 {
  //     padding: 0.6em 0 3px;
  //     margin: 0;
  //     line-height: 1.25em;
  //     outline: none;
  //     font-weight: 700;
  //   }

  //   ol,
  //   ul {
  //     margin: 0;
  //     padding-left: 40px;
  //     outline: none;

  //     p {
  //       padding: 0;
  //       margin: 0;
  //     }
  //   }

  //   ol {
  //     list-style: decimal;
  //   }

  //   ul {
  //     list-style: disc;
  //   }

  //   li {
  //     padding: 5.5px 0 5.5px 3px;
  //     line-height: 1.6em;
  //   }

  //   iframe,
  //   img,
  //   video {
  //     width: 100%;
  //     border-radius: 16px;
  //   }
  // }
</style>
